/**
 * Turns a report model into a single self-contained, offline HTML file and downloads it.
 *
 * Security:
 * - The model is embedded as JSON in a non-executable <script type="application/json"> with
 *   "<", U+2028 and U+2029 escaped, and the runtime reads it with JSON.parse.
 * - A Content-Security-Policy allows only the inlined runtime script (pinned by SHA-256),
 *   inline styles and data: fonts/images, and blocks every network request.
 * - All model text is rendered by React as text nodes; nothing is inserted as HTML.
 */
import { saveAs } from "file-saver";
import { sanitizeFileName } from "../functions/results/formatters";
import { REPORT_DATA_ELEMENT_ID, buildReportStrings } from "./reportStrings";

// Banner written by scripts/build-report-runtime.js at the top of both runtime files.
export const RUNTIME_MARKER = "/* arivu-report-runtime */";

export class ReportRuntimeUnavailableError extends Error {}

export const RUNTIME_ASSETS = {
  script: "report-runtime/runtime.js",
  style: "report-runtime/runtime.css",
};

const fetchRuntimeAsset = async (assetPath) => {
  const response = await fetch(`${process.env.PUBLIC_URL || ""}/${assetPath}`, { credentials: "omit", cache: "no-cache" });
  const text = response.ok ? await response.text() : "";
  // The dev server answers missing files with index.html, so check the build marker, not just the status.
  if (!text.startsWith(RUNTIME_MARKER)) {
    throw new ReportRuntimeUnavailableError(
      `Report runtime asset ${assetPath} is missing or invalid (status ${response.status}). Run "npm run build:report-runtime".`
    );
  }
  return text;
};

/** JSON safe to place inside a <script> element. */
export const serializeForScriptTag = (data) =>
  JSON.stringify(data).replace(/</g, "\\u003c").replace(/\u2028/g, "\\u2028").replace(/\u2029/g, "\\u2029");

/** Prevents inlined code from closing its own <script>/<style> element early. */
export const escapeInlineCode = (code, tagName) => code.replace(new RegExp(`</(${tagName})`, "gi"), "<\\/$1").replace(/<!--/g, "<\\!--");

export const escapeHtmlText = (text) =>
  String(text ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]);

const LANGUAGE_TAG = /^[a-zA-Z]{2,8}(-[a-zA-Z0-9]{1,8})*$/;

const sha256Base64 = async (text) => {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(text));
  return btoa(String.fromCharCode(...new Uint8Array(digest)));
};

export const buildContentSecurityPolicy = (scriptHash) =>
  [
    "default-src 'none'",
    `script-src 'sha256-${scriptHash}'`,
    "style-src 'unsafe-inline'",
    "img-src data: blob:",
    "font-src data:",
    "base-uri 'none'",
    "form-action 'none'",
  ].join("; ");

/**
 * @param {object} model - report model (see src/reports/model)
 * @param {string} script - runtime JavaScript
 * @param {string} style - runtime CSS
 * @returns {Promise<string>} complete HTML document
 */
export const buildReportHtml = async ({ model, script, style }) => {
  const safeScript = escapeInlineCode(script, "script");
  const safeStyle = escapeInlineCode(style, "style");
  const scriptHash = await sha256Base64(safeScript);
  const language = LANGUAGE_TAG.test(model.meta.language) ? model.meta.language : "en";

  return [
    "<!doctype html>",
    `<html lang="${language}">`,
    "<head>",
    '<meta charset="utf-8">',
    `<meta http-equiv="Content-Security-Policy" content="${buildContentSecurityPolicy(scriptHash)}">`,
    '<meta name="viewport" content="width=device-width, initial-scale=1">',
    '<meta name="referrer" content="no-referrer">',
    `<title>${escapeHtmlText(model.meta.entityName)}</title>`,
    `<style>${safeStyle}</style>`,
    "</head>",
    "<body>",
    '<div id="root"></div>',
    `<script type="application/json" id="${REPORT_DATA_ELEMENT_ID}">${serializeForScriptTag(model)}</script>`,
    `<script>${safeScript}</script>`,
    "</body>",
    "</html>",
  ].join("\n");
};

/**
 * Adds translated UI strings to the model, builds the HTML and downloads it.
 *
 * @param {object} model - report model without `strings`
 * @param {function} translate - i18n t function
 * @param {string} fileLabel - e.g. "BRSR_Report"; combined with entity name and year
 */
export const generateHtmlReport = async ({ model, translate, fileLabel }) => {
  const [script, style] = await Promise.all([fetchRuntimeAsset(RUNTIME_ASSETS.script), fetchRuntimeAsset(RUNTIME_ASSETS.style)]);
  const html = await buildReportHtml({ model: { ...model, strings: buildReportStrings(translate) }, script, style });
  const fileName = `${sanitizeFileName(["Arivu", model.meta.entityName, fileLabel, model.meta.reportingYear].filter(Boolean).join("_"))}.html`;
  saveAs(new Blob([html], { type: "text/html;charset=utf-8" }), fileName);
};
